
import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { ArrowLeft, ArrowRight, BarChart3 } from 'lucide-react';

interface ConsumptionDataPoint {
  date: string;
  kwh: number;
  cost: number;
  period?: string;
}

interface ConsumptionChartProps {
  showControls?: boolean;
  height?: string;
  defaultPeriod?: 'day' | 'week' | 'month' | 'year';
  showPriceLabels?: boolean;
  realData?: ConsumptionDataPoint[]; // Accept real data as prop
  pricePerKwh?: number; // Rate from building
  onPeriodChange?: (period: 'day' | 'week' | 'month' | 'year') => void; // Callback when period changes
  externalPeriod?: 'day' | 'week' | 'month' | 'year'; // Controlled period from parent
}

const ConsumptionChart: React.FC<ConsumptionChartProps> = ({
  showControls = true,
  height = 'h-64',
  defaultPeriod = 'month',
  showPriceLabels = false,
  realData,
  pricePerKwh: propPricePerKwh,
  onPeriodChange,
  externalPeriod
}) => {
  const [internalPeriod, setInternalPeriod] = useState<'day' | 'week' | 'month' | 'year'>(defaultPeriod);
  const period = externalPeriod ?? internalPeriod; // Use external if provided
  const [viewMode, setViewMode] = useState<'kwh' | 'TZS'>('kwh');
  const [currentIndex, setCurrentIndex] = useState(0);

  // Check if we have real data
  const hasRealData = realData && realData.length > 0;
  const data = hasRealData ? realData : [];

  const handlePeriodChange = (newPeriod: 'day' | 'week' | 'month' | 'year') => {
    setInternalPeriod(newPeriod);
    // Reset to show latest data when period changes
    if (onPeriodChange) {
      onPeriodChange(newPeriod);
    }
  };

  // Reset currentIndex to latest when data changes
  React.useEffect(() => {
    if (data.length > 0) {
      setCurrentIndex(data.length - 1);
    }
  }, [data.length, period]);

  // Calculate the window of data to show (7 items centered around currentIndex)
  const windowSize = 7;
  const effectiveIndex = Math.min(currentIndex, data.length - 1);
  const startIndex = Math.max(0, Math.min(effectiveIndex - Math.floor(windowSize / 2), data.length - windowSize));
  const endIndex = Math.min(startIndex + windowSize, data.length);

  // Get data points for the chart window
  const chartData = data.slice(startIndex, endIndex).map((item, index) => ({
    name: formatPeriodLabel(item.date, period, true),
    value: viewMode === 'kwh' ? item.kwh : item.cost,
    kwh: item.kwh,
    cost: item.cost,
    fullDate: item.date,
    isSelected: startIndex + index === effectiveIndex // Highlight the current selected period
  }));

  const currentData = data[effectiveIndex];
  const pricePerKwh = propPricePerKwh || 0;

  // Empty state when no real data
  if (!hasRealData) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Consumption</h3>
        </div>
        <div className={`w-full ${height} flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600`}>
          <div className="text-center p-8">
            <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Consumption Data</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-sm">
              Your smart meter hasn't been set up yet. Once your meter is configured, you'll see your real-time energy consumption here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  function formatPeriodLabel(dateStr: string, period: string, short = false) {
    if (!dateStr) return 'N/A';

    try {
      if (period === 'week') {
        // Handle new format: YYYY-MM-DD (week start date)
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return dateStr;
          const endDate = new Date(date.getTime() + 6 * 24 * 60 * 60 * 1000);
          return short
            ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        }
        // Handle old format: YYYY-MM-WNN
        const weekNum = dateStr.split('-W')[1];
        return short ? `W${weekNum}` : `Week ${weekNum}`;
      }
      if (period === 'month') {
        // Handle YYYY-MM format
        const parts = dateStr.split('-');
        if (parts.length === 2) {
          const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1);
          if (isNaN(date.getTime())) return dateStr;
          return short
            ? date.toLocaleDateString('en-US', { month: 'short' })
            : date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        }
        // Handle YYYY-MM-DD format
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        return short
          ? date.toLocaleDateString('en-US', { month: 'short' })
          : date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      }
      if (period === 'year') {
        return dateStr;
      }
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return short
        ? date.toLocaleDateString('en-US', { day: 'numeric' })
        : date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    } catch {
      return dateStr || 'N/A';
    }
  }

  const navigatePeriod = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else if (direction === 'next' && currentIndex < data.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ payload: { kwh: number; cost: number } }>; label?: string }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 dark:text-white">{label}</p>
          <p className="text-blue-600">{`Consumption: ${data.kwh} kWh`}</p>
          <p className="text-green-600">{`Cost: TZS ${data.cost.toLocaleString()}`}</p>
          {showPriceLabels && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{`TZS ${pricePerKwh.toLocaleString()}/kWh`}</p>
          )}
        </div>
      );
    }
    return null;
  };

  const CustomBarLabel = ({ x, y, width, value }: { x?: number; y?: number; width?: number; value?: number }) => {
    if (!showPriceLabels) return null;
    
    return (
      <text
        x={x + width / 2}
        y={y - 5}
        textAnchor="middle"
        className="fill-gray-600 dark:fill-gray-400 text-xs font-medium"
      >
        {viewMode === 'kwh' ? `${value} kWh` : `TZS ${value?.toLocaleString()}`}
      </text>
    );
  };

  return (
    <div className="space-y-4">
      {showControls && (
        <>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Consumption</h3>
            <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('kwh')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'kwh' 
                    ? 'bg-white dark:bg-gray-600 text-blue-500 shadow-sm' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                kWh
              </button>
              <button
                onClick={() => setViewMode('TZS')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'TZS'
                    ? 'bg-white dark:bg-gray-600 text-blue-500 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                TZS
              </button>
            </div>
          </div>

          <div className="flex space-x-1">
            {(['day', 'week', 'month', 'year'] as const).map((p) => (
              <button
                key={p}
                onClick={() => handlePeriodChange(p)}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                  period === p
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {p === 'day' ? 'Day' : p === 'week' ? 'Week' : p === 'month' ? 'Month' : 'Year'}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Chart */}
      <div className={`w-full ${height}`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <XAxis 
              dataKey="name" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="value" 
              radius={[4, 4, 0, 0]}
              label={<CustomBarLabel />}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.isSelected ? '#1E40AF' : '#3B82F6'} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {showPriceLabels && pricePerKwh > 0 && (
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Current rate: <span className="font-semibold text-blue-600">TZS {pricePerKwh.toLocaleString()}/kWh</span>
          </p>
        </div>
      )}

      {showControls && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigatePeriod('prev')}
            disabled={currentIndex === 0}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          
          <div className="text-center">
            <h4 className="font-semibold text-gray-900 dark:text-white">
              {currentData ? formatPeriodLabel(currentData.date, period) : 'N/A'}
            </h4>
            <div className="text-2xl font-bold text-blue-500 mt-1">
              {currentData ? (viewMode === 'kwh' ? `${currentData.kwh} kWh` : `TZS ${currentData.cost.toLocaleString()}`) : '--'}
            </div>
          </div>
          
          <button
            onClick={() => navigatePeriod('next')}
            disabled={currentIndex === data.length - 1}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowRight size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ConsumptionChart;
